import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Clock, ArrowLeft, Share2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { BLOG_POSTS } from '@/lib/data';

export default function BlogDetail() {
  const { id } = useParams();
  const post = BLOG_POSTS.find(p => p.id === id);

  if (!post) return (
    <div className="pt-32 text-center">
      <h1 className="font-heading text-2xl font-bold">Post not found</h1>
      <Link to="/blog" className="text-primary mt-4 inline-block">← Back to Blog</Link>
    </div>
  );

  const content = `
This is a comprehensive guide that explores the key aspects of "${post.title}". 

## Introduction

The tech industry continues to evolve at an unprecedented pace. Whether you're a beginner or an experienced professional, staying updated with the latest trends is crucial for career growth.

## Key Takeaways

1. **Continuous Learning is Essential** - The most successful professionals never stop learning. They dedicate time each week to expanding their skillset.

2. **Practical Experience Matters** - Building real-world projects is the fastest way to solidify your knowledge and build a compelling portfolio.

3. **Community & Networking** - Connecting with peers, mentors, and industry professionals opens doors to opportunities you might not find on your own.

## The Path Forward

Start by identifying your goals and working backward to create a learning roadmap. XceedEdge offers structured programs designed to take you from where you are to where you want to be.

## Conclusion

The best time to invest in your tech career is now. With the right resources, guidance, and dedication, you can achieve remarkable results in a surprisingly short time.
  `;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="pt-24 pb-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
          <Link to="/" className="hover:text-primary">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/blog" className="hover:text-primary">Blog</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-foreground truncate">{post.title}</span>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <Badge variant="secondary">{post.category}</Badge>
          <span className="text-sm text-muted-foreground flex items-center gap-1">
            <Clock className="w-3 h-3" /> {post.readTime}
          </span>
          <span className="text-sm text-muted-foreground">
            {new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </span>
        </div>

        <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-6">{post.title}</h1>

        <div className="prose prose-slate max-w-none">
          {content.split('\n').map((line, i) => {
            if (line.startsWith('## ')) return <h2 key={i} className="font-heading text-xl font-bold mt-8 mb-3">{line.replace('## ', '')}</h2>;
            if (line.startsWith('1. ') || line.startsWith('2. ') || line.startsWith('3. ')) {
              const boldMatch = line.match(/\*\*(.*?)\*\*/);
              return (
                <p key={i} className="mb-2 text-muted-foreground leading-relaxed">
                  {boldMatch ? <><strong className="text-foreground">{boldMatch[1]}</strong>{line.split('**')[2]}</> : line}
                </p>
              );
            }
            if (line.trim()) return <p key={i} className="text-muted-foreground leading-relaxed mb-4">{line}</p>;
            return null;
          })}
        </div>

        <div className="flex items-center gap-2 mt-8 pt-6 border-t">
          {post.tags.map(tag => (
            <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
          ))}
        </div>

        <div className="flex items-center justify-between mt-6">
          <Link to="/blog"><Button variant="outline" className="gap-2"><ArrowLeft className="w-4 h-4" /> Back to Blog</Button></Link>
          <Button variant="outline" className="gap-2"><Share2 className="w-4 h-4" /> Share</Button>
        </div>
      </div>
    </motion.div>
  );
}